/**
 * Die Bildflaeche des Einstiegs (/welcome): links neben der Frage, am Telefon
 * als schmaler Streifen darueber.
 *
 * Entwurf „Einstieg nach Higgsfield“, Variante B „Vorschau links“ (Seite im
 * Design-Projekt „99takes — Dashboard run.99takes.com“, gewaehlt von Robert
 * am 05.11.2026). Higgsfield zeigt links ein grosses Bild, das je Frage
 * wechselt. Bei 99takes zeigt die Flaeche stattdessen, was die Antworten
 * bringen:
 *
 *   satz    der erste Satz fuer Claude, aus den bisherigen Antworten
 *           (ersterLaufVorschlag), dazu was es kostet
 *   leute   wer mitliest: vier Portraets, eine Zielgruppe hat 10 bis 14 Personas
 *   claude  wie 99takes in Claude aussieht: die Zeile mit dem Namen 99takes,
 *           derselbe Test-Satz wie in der Anleitung
 *
 * Die Flaeche ist immer dunkel (`nacht`, `minz`), auch im hellen Modus, wie die
 * dunklen Bloecke der Website. Die Portraets sind KI-erzeugt (dieselben wie auf
 * der Startseite von 99takes.com, 240 px, public/bilder/) und zeigen fiktive
 * Personen; der Hinweis „KI generiert“ steht immer dabei.
 */

import type { ReactNode } from 'react';
import { EINRICHTUNG, VERGLEICH_1ZU1, VERGLEICH_STANDARD } from '../preise';
import { locale, t } from '../sprache';
import { Zeichen } from '../ui';
import { TESTSATZ, TESTSATZ_EN } from './Einfuehrung';

export type FlaechenArt = 'satz' | 'leute' | 'claude';

/** Die vier Portraets der Flaeche, in dieser Reihenfolge. */
export const PORTRAETS = ['/bilder/marketing.webp', '/bilder/kundin.webp', '/bilder/agentur.webp', '/bilder/waehler.webp'] as const;

/** Portraets an den Antworten auf „Wen wollt ihr erreichen?“. Ohne Eintrag: ein Zeichen statt Bild. */
export const ANTWORT_BILDER: Record<string, readonly string[]> = {
  b2b: ['/bilder/marketing.webp'],
  b2c: ['/bilder/kundin.webp'],
  beides: ['/bilder/marketing.webp', '/bilder/kundin.webp'],
  politik: ['/bilder/waehler.webp'],
};

const EB = 'text-[0.6875rem] font-bold tracking-[0.08em] text-minz uppercase';

export function Flaeche({ art, satz, zurueck, guthaben = null, klein = false }: {
  art: FlaechenArt;
  /** Fuer `satz`: der Satz und was zurueckkommt. */
  satz?: string;
  zurueck?: string;
  /** Fuer `claude`: das Guthaben in der gezeichneten Antwort, ohne Zahl ohne Satz dazu. */
  guthaben?: number | null;
  /** Der Streifen am Telefon. */
  klein?: boolean;
}) {
  const inhalt: ReactNode = art === 'leute' ? <Leute klein={klein} /> : art === 'claude' ? <Claude klein={klein} guthaben={guthaben} /> : <Satz klein={klein} satz={satz ?? ''} zurueck={zurueck} />;
  if (klein) {
    return (
      <div className="mb-4 flex flex-col gap-2 rounded-[0.875rem] border border-white/10 bg-nacht px-3.5 py-3 text-[#c9d4d2]">
        {inhalt}
      </div>
    );
  }
  return (
    <div className="relative flex h-full flex-col justify-end gap-3.5 overflow-hidden rounded-[1.125rem] border border-white/10 bg-nacht p-7 text-[#c9d4d2]">
      <div className="absolute top-6 left-7 flex items-center gap-2 text-[1.0625rem] font-bold tracking-[-0.02em] text-[#f4f7f6]">
        <Zeichen className="size-6" />99takes
      </div>
      {inhalt}
    </div>
  );
}

function Satz({ satz, zurueck, klein }: { satz: string; zurueck?: string; klein: boolean }) {
  return (
    <>
      <span className={EB}>{t('Dein Start in Claude', 'Your start in Claude')}</span>
      <p className={`m-0 border border-[#26302e] bg-[#18201e] text-[#f4f7f6] ${klein ? 'line-clamp-3 rounded-[0.625rem] px-3 py-2 text-[0.8125rem] leading-snug' : 'rounded-[0.875rem] p-4 text-[1.0625rem] leading-relaxed'}`}>
        {t(`„${satz}“`, `“${satz}”`)}
      </p>
      {!klein && zurueck && <p className="m-0 text-[0.84375rem] leading-relaxed text-[#b3bfbd]">{zurueck}</p>}
      {/* Was es kostet, als zwei Zeilen statt eines Satzes (Feedback
          Testnutzer, 15.10.2026). Die Spanne ist dieselbe Rechnung wie in
          preise.ts: 12 Personas als 1:1 bis Persona × Job. */}
      {!klein && (
        <div className="mt-2 border-t border-[#26302e] pt-3.5">
          <div className={EB}>{t('Was es kostet', 'What it costs')}</div>
          <dl className="m-0 mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[0.8125rem] leading-snug">
            <dt className="text-[#b3bfbd]">{t('Deine Zielgruppe anlegen', 'Create your audience')}</dt>
            <dd className="m-0 text-right font-semibold whitespace-nowrap text-[#f4f7f6] tabular-nums">{t(<>{EINRICHTUNG} Credits, einmal</>, <>{EINRICHTUNG} credits, once</>)}</dd>
            <dt className="text-[#b3bfbd]">{t('Ein Vergleich zweier Texte', 'A comparison of two texts')}</dt>
            <dd className="m-0 text-right font-semibold whitespace-nowrap text-[#f4f7f6] tabular-nums">{t(<>{VERGLEICH_1ZU1} bis {VERGLEICH_STANDARD} Credits</>, <>{VERGLEICH_1ZU1} to {VERGLEICH_STANDARD} credits</>)}</dd>
          </dl>
          <p className="m-0 mt-2.5 text-[0.75rem] leading-relaxed text-[#93a09e]">
            {t('Claude nennt dir den Preis, bevor etwas gebucht wird, und abgebucht wird zuerst vom Startguthaben.',
              'Claude tells you the price before anything is charged, and it comes out of your starting credits first.')}
          </p>
        </div>
      )}
    </>
  );
}

function Leute({ klein }: { klein: boolean }) {
  return (
    <>
      <div className="flex gap-2">
        {PORTRAETS.map(b => (
          <img key={b} src={b} alt="" width={240} height={240} className={`${klein ? 'size-10' : 'size-[4.75rem]'} rounded-full bg-[#26302e] object-cover`} />
        ))}
      </div>
      {!klein && <span className={`${EB} mt-1.5`}>{t('Wer deine Texte liest', 'Who reads your copy')}</span>}
      <p className={`m-0 ${klein ? 'text-[0.78125rem] leading-snug text-[#b3bfbd]' : 'text-[1rem] leading-relaxed text-[#f4f7f6]'}`}>
        {klein
          ? t('Eine Zielgruppe hat 10 bis 14 Personas.', 'An audience has 10 to 14 personas.')
          : t('Eine Zielgruppe hat 10 bis 14 Personas. Hast du noch keine, baut Claude sie im ersten Run mit dir auf.',
            "An audience has 10 to 14 personas. If you don't have one yet, Claude builds it with you in your first run.")}
        {klein && <> {t('Beispielbilder, KI generiert.', 'Example images, AI generated.')}</>}
      </p>
      {!klein && <span className="text-[0.75rem] text-[#93a09e]">{t('Beispielbilder, KI generiert', 'Example images, AI generated')}</span>}
    </>
  );
}

function Claude({ klein, guthaben }: { klein: boolean; guthaben: number | null }) {
  const zahl = guthaben != null && guthaben > 0 ? new Intl.NumberFormat(locale()).format(guthaben) : null;
  return (
    <>
      {!klein && <span className={EB}>{t('So sieht es in Claude aus', 'This is how it looks in Claude')}</span>}
      {/* Gezeichnet, kein Bildschirmfoto: wie claude.ai einen Werkzeugaufruf
          genau darstellt, ist nicht belegt (Einfuehrung.tsx, „Erkennen“). */}
      <div aria-label={t('Ein Chat mit Claude, gezeichnet', 'A chat with Claude, drawn')} role="img"
        className={`flex flex-col border border-[#26302e] bg-[#18201e] ${klein ? 'gap-1.5 rounded-[0.625rem] p-2.5' : 'gap-2.5 rounded-[0.875rem] p-3.5'}`}>
        <div className={`self-end rounded-xl bg-[#2b3433] text-[#f4f7f6] ${klein ? 'max-w-[92%] px-2.5 py-1.5 text-[0.75rem] leading-snug' : 'max-w-[88%] px-3 py-2 text-[0.8125rem] leading-snug'}`}>
          {t(TESTSATZ, TESTSATZ_EN)}
        </div>
        <div className={`flex items-center gap-2 rounded-[0.625rem] border border-marke bg-[rgb(95_179_173/0.1)] font-semibold text-[#f4f7f6] ${klein ? 'px-2 py-1 text-[0.75rem]' : 'px-2.5 py-1.5 text-[0.78125rem]'}`}>
          <Zeichen className="size-4" />99takes<span aria-hidden className="ml-auto text-minz">›</span>
        </div>
        {!klein && (
          <p className="m-0 text-[0.8125rem] leading-relaxed">
            {zahl
              ? t(`Ja, 99takes ist verbunden. Auf deinem Konto liegen ${zahl} Credits.`, `Yes, 99takes is connected. There are ${zahl} credits in your account.`)
              : t('Ja, 99takes ist verbunden.', 'Yes, 99takes is connected.')}
          </p>
        )}
      </div>
      <p className={`m-0 leading-relaxed text-[#b3bfbd] ${klein ? 'text-[0.75rem]' : 'text-[0.84375rem]'}`}>
        {klein
          ? t('Daran erkennst du es: die Zeile mit dem Namen 99takes.', 'This is how you can tell: the line named 99takes.')
          : t('Daran erkennst du es: die Zeile mit dem Namen 99takes. Fehlt sie, hat Claude ohne 99takes geantwortet.',
            'This is how you can tell: the line named 99takes. If it is missing, Claude answered without 99takes.')}
      </p>
    </>
  );
}
